/**
 * <ImageIcon> — a server-provided raster illustration atom.
 *
 * Renders a remote image (e.g. a school-subject icon URL) at a square size. Unlike
 * <Icon> (vector glyphs), these are full-color images and are NOT tinted. Renders
 * nothing when no `uri` is provided.
 *
 * La pastille. Ces pictos sont dessinés pour un fond blanc, et certains sont marine (le « √ »
 * des maths) : posés sur un aplat foncé, ils disparaissent. Ils s'y posent alors sur une pastille
 * claire — la même empreinte, le picto un peu plus petit dedans, donc rien ne bouge autour.
 * Deux cas : l'appelant la demande (`plate`, une puce de matière choisie, pleine de sa couleur),
 * et le thème sombre, où toutes les surfaces sont foncées. Ce second cas lit `useIsDarkTheme` :
 * ce n'est pas une couleur qui change, c'est un dessin qui apparaît.
 *
 * `dimmed` estompe le picto, pastille comprise (jeton `dimming`) : celui d'une séance où l'enfant
 * ne sera pas.
 */
import React from "react"
import { Image, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { useIsDarkTheme } from "../../../hooks/useIsDarkTheme"
import { dimming } from "../../../theme/tokens/effects"

export type ImageIconProps = {
    /** Remote image URL (e.g. a server subject icon). */
    uri?: string
    /** Square pixel size — defaults to 32. */
    size?: number
    /**
     * Poser le picto sur sa pastille claire, parce que le fond sous lui est un aplat foncé (une
     * puce choisie). Défaut `false` ; en thème sombre, la pastille est toujours là.
     */
    plate?: boolean
    /** Estompé : le picto d'une séance où l'enfant ne sera pas. Défaut `false`. */
    dimmed?: boolean
    testID?: string
}

export function ImageIcon(props: ImageIconProps) {
    const { uri, size = 32, plate = false, dimmed = false, testID } = props
    const onNight = useIsDarkTheme()

    if (!uri) return null

    if (!plate && !onNight) {
        return (
            <Image
                testID={testID}
                source={{ uri }}
                style={[styles.image(size), dimmed ? styles.dimmed : undefined]}
                resizeMode="contain"
            />
        )
    }

    return (
        <View style={[styles.plate(size), dimmed ? styles.dimmed : undefined]}>
            <Image
                testID={testID}
                source={{ uri }}
                style={styles.image(size - 2 * plateInset(size))}
                resizeMode="contain"
            />
        </View>
    )
}

/** Le bord de pastille autour du picto : environ un septième de l'empreinte, jamais moins de 2 px. */
function plateInset(size: number): number {
    return Math.max(2, Math.round(size * 0.14))
}

const styles = StyleSheet.create((theme) => ({
    image: (size: number) => ({
        width: size,
        height: size,
    }),
    dimmed: {
        opacity: dimming.opacity,
    },
    plate: (size: number) => ({
        width: size,
        height: size,
        borderRadius: size / 2,
        // `white` ne s'inverse pas : la pastille reste claire sur la nuit, c'est tout son rôle.
        backgroundColor: theme.colors.white,
        alignItems: "center" as const,
        justifyContent: "center" as const,
    }),
}))
